import { useTranslation } from 'react-i18next';
import './FitMannequin.css';

// Les épaules sont toujours calculées côté backend mais ne sont plus affichées : elles ne
// sont plus déterminantes pour la taille recommandée (voir CHECKED_ZONES_BY_TYPE côté backend).
const ZONES_BY_GARMENT = {
  top:    ['chest', 'waist'],
  bottom: ['waist', 'hips'],
  full:   ['chest', 'waist', 'hips'],
};

const FIT_COLORS = {
  ideal:    '#22c55e',
  fit:      '#f59e0b',
  oversize: '#ef4444',
};

// ─── POSITIONS VERTICALES ────────────────────────────────────────────────────
// En % de la hauteur totale du PNG (480 × 1280), par genre ET par type de vêtement.
// Les zones sont aux mêmes positions absolues dans l'image quelle que soit la coupe,
// mais chaque config est indépendante pour permettre un calibrage précis.
const ZONE_TOP_PCT = {
  femme: {
    top:    { shoulder: 20, chest: 28, waist: 42 },
    bottom: { waist: 42, hips: 47 },
    full:   { chest: 28, waist: 42, hips: 47 },
  },
  homme: {
    top:    { shoulder: 21, chest: 29, waist: 40 },
    bottom: { waist: 40, hips: 48 },
    full:   { chest: 29, waist: 40, hips: 48 },
  },
};

// ─── CONFIG DES BANDES ───────────────────────────────────────────────────────
// cx : centre horizontal en % de CONTAINER_W
// rx : demi-largeur horizontale en % de CONTAINER_W
// ry : rayon vertical en px (platitude de l'ellipse = perspective 3/4)
// Chaque type de vêtement a sa propre config pour permettre un calibrage indépendant.
// curve: 'down' → arc passe sous cy (∪, sweep=1) — défaut
// curve: 'up'   → arc passe au-dessus de cy (∩, sweep=0)
const BAND_CONFIG = {
  femme: {
    top: {
      shoulder: { cx: 45,   rx: 25, ry: 4,   curve: 'down' },
      chest:     { cx: 51.5, rx: 23, ry: 7,   curve: 'down' },
      waist:     { cx: 49,   rx: 23, ry: 5.5, curve: 'down' },
    },
    bottom: {
      waist: { cx: 49, rx: 23, ry: 4, curve: 'down' },
      hips:  { cx: 47, rx: 25, ry: 7, curve: 'up' },
    },
    full: {
      chest: { cx: 51.5, rx: 23, ry: 7,   curve: 'down' },
      waist: { cx: 49,   rx: 23, ry: 4,   curve: 'down' },
      hips:  { cx: 47,   rx: 25, ry: 7,   curve: 'up' },
    },
  },
  homme: {
    top: {
      shoulder: { cx: 46,   rx: 25, ry: 4,   curve: 'down' },
      chest:     { cx: 49.5, rx: 22, ry: 6,   curve: 'down' },
      waist:     { cx: 49,   rx: 21, ry: 5.5, curve: 'down' },
    },
    bottom: {
      waist: { cx: 49, rx: 21, ry: 5.5, curve: 'down' },
      hips:  { cx: 47, rx: 23, ry: 6,   curve: 'up' },
    },
    full: {
      chest: { cx: 49.5, rx: 22, ry: 6,   curve: 'down' },
      waist: { cx: 49,   rx: 21, ry: 5.5, curve: 'down' },
      hips:  { cx: 47,   rx: 23, ry: 6,   curve: 'up' },
    },
  },
};

// ─── ROGNAGE PAR TYPE DE VÊTEMENT ────────────────────────────────────────────
// start / end = % de la hauteur totale de l'image à afficher
const CROP = {
  top:    { start: 0,    end: 0.58 },  // tête → dessous de la taille
  bottom: { start: 0.30, end: 0.65 },  // taille → dessous des hanches
  full:   { start: 0,    end: 0.62 },  // tête → dessous des hanches
};

// Rapport hauteur/largeur : 1280 / 480 ≈ 2.667
const IMG_RATIO   = 1280 / 480;
const CONTAINER_W = 155;
const LABEL_COL_W = 75;

function FitMannequin({ fitType = 'ideal', gender = 'femme', garmentType = 'top', fitZones = null, mannequinSrc = null, customBandConfig = null }) {
  const { t } = useTranslation('results');
  const g = gender === 'homme' ? 'homme' : 'femme';

  const gt           = ZONES_BY_GARMENT[garmentType] ? garmentType : 'top';
  const activeZones  = ZONES_BY_GARMENT[gt];
  const zonePct      = ZONE_TOP_PCT[g][gt];
  const bandCfg      = BAND_CONFIG[g][gt];

  const getColor = (zoneName) => {
    if (fitZones) {
      const z = fitZones.find(z => z.zone === zoneName);
      return z ? z.color : FIT_COLORS.ideal; // ne sera atteint que si fitZones est fourni et contient la zone
    }
    return FIT_COLORS[fitType] || FIT_COLORS.ideal;
  };

  const getLabelKey = (zoneName) => {
    if (!fitZones) return null;
    const z = fitZones.find(z => z.zone === zoneName);
    return z?.labelKey || null;
  };

  const crop       = CROP[garmentType] || CROP.full;
  const fullImgH   = Math.round(CONTAINER_W * IMG_RATIO);           // ≈ 414 px
  const containerH = Math.round(fullImgH * (crop.end - crop.start));
  const imgTop     = Math.round(-fullImgH * crop.start);

  // Calcule les zones visibles avec leurs coordonnées SVG (px)
  const visibleZones = activeZones.flatMap((zoneName) => {
    const band = bandCfg[zoneName];
    if (!band) return [];

    // Si fitZones est fourni, n'afficher que les zones présentes dans la réponse backend
    if (fitZones && !fitZones.find(z => z.zone === zoneName)) return [];

    const topPct    = zonePct[zoneName] / 100;
    const cropRange = crop.end - crop.start;
    const topInCrop = ((topPct - crop.start) / cropRange) * 100;
    if (topInCrop < 0 || topInCrop > 100) return [];

    const color    = getColor(zoneName);
    const labelKey = getLabelKey(zoneName);
    const cy    = (topInCrop / 100) * containerH;
    const cxPx  = (band.cx / 100) * CONTAINER_W;
    const effectiveRx = customBandConfig?.[zoneName]?.rx ?? band.rx;
    const rxPx  = (effectiveRx / 100) * CONTAINER_W;

    return [{ zoneName, color, labelKey, cy, cxPx, rxPx, ry: band.ry, curve: band.curve || 'down' }];
  });

  // Assigne les côtés gauche/droite : la zone la plus basse (cy max) est toujours à gauche,
  // puis alternance droite/gauche en remontant.
  const zonesWithLabels = visibleZones.filter(z => z.labelKey);
  const sortedByBottom  = [...zonesWithLabels].sort((a, b) => b.cy - a.cy);
  const sideMap = new Map(sortedByBottom.map((z, i) => [z.zoneName, i % 2 === 0 ? 'left' : 'right']));

  const zonesWithSides = visibleZones.map((z) => ({
    ...z,
    side: z.labelKey ? (sideMap.get(z.zoneName) || null) : null,
  }));

  const leftZones  = zonesWithSides.filter(z => z.side === 'left');
  const rightZones = zonesWithSides.filter(z => z.side === 'right');
  const hasLabels  = zonesWithLabels.length > 0;

  const imgSrc = mannequinSrc || `${process.env.PUBLIC_URL}/mannequin_${g}.png`;

  return (
    <div className="fit-mannequin">
      {/* Colonne de labels gauche */}
      {hasLabels && (
        <div
          className="fit-mannequin__label-col fit-mannequin__label-col--left"
          style={{ width: LABEL_COL_W, height: containerH }}
        >
          {leftZones.map(({ zoneName, color, labelKey, cy }) => (
            <div
              key={zoneName}
              className="fit-mannequin__label fit-mannequin__label--left"
              style={{ top: cy - 16, borderRightColor: color }}
            >
              <span className="fit-mannequin__label-zone">{t(`fitMannequin.zones.${zoneName}`)}</span>
              <span className="fit-mannequin__label-fit">{t(`fitMannequin.fitLabels.${labelKey}`)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Mannequin + overlay SVG */}
      <div
        className="fit-mannequin__wrap"
        style={{ width: CONTAINER_W, height: containerH }}
      >
        {/* Image mannequin selon le genre */}
        <img
          src={imgSrc}
          className="fit-mannequin__img"
          style={{ width: CONTAINER_W, height: fullImgH, top: imgTop }}
          alt=""
          draggable="false"
        />

        {/* Overlay SVG — bandes enveloppantes */}
        <svg
          className="fit-mannequin__overlay"
          viewBox={`0 0 ${CONTAINER_W} ${containerH}`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/* Glow : flou léger superposé à la source */}
            <filter id="fm-glow" x="-30%" y="-100%" width="160%" height="300%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="1.8" result="blur"/>
              <feMerge>
                <feMergeNode in="blur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>

            {/* Dégradé horizontal : opaque au centre, invisible aux extrémités */}
            {visibleZones.map(({ zoneName, color, cxPx, rxPx, cy }) => (
              <linearGradient
                key={`g-${zoneName}`}
                id={`g-${zoneName}`}
                gradientUnits="userSpaceOnUse"
                x1={cxPx - rxPx} y1={cy}
                x2={cxPx + rxPx} y2={cy}
              >
                <stop offset="0%"   stopColor={color} stopOpacity="0.0"/>
                <stop offset="10%"  stopColor={color} stopOpacity="0.85"/>
                <stop offset="50%"  stopColor={color} stopOpacity="1.0"/>
                <stop offset="90%"  stopColor={color} stopOpacity="0.85"/>
                <stop offset="100%" stopColor={color} stopOpacity="0.0"/>
              </linearGradient>
            ))}
          </defs>

          {visibleZones.map(({ zoneName, cy, cxPx, rxPx, ry, curve }) => {
            const x1    = cxPx - rxPx;
            const x2    = cxPx + rxPx;
            // sweep=1 → arc passe sous cy (∪)  |  sweep=0 → arc passe au-dessus (∩)
            const sweep = curve === 'up' ? 0 : 1;
            const frontArc = `M ${x1},${cy} A ${rxPx},${ry} 0 0 ${sweep} ${x2},${cy}`;

            return (
              <path
                key={zoneName}
                d={frontArc}
                fill="none"
                stroke={`url(#g-${zoneName})`}
                strokeWidth="5"
                strokeLinecap="round"
                filter="url(#fm-glow)"
              />
            );
          })}
        </svg>
      </div>

      {/* Colonne de labels droite */}
      {hasLabels && (
        <div
          className="fit-mannequin__label-col fit-mannequin__label-col--right"
          style={{ width: LABEL_COL_W, height: containerH }}
        >
          {rightZones.map(({ zoneName, color, labelKey, cy }) => (
            <div
              key={zoneName}
              className="fit-mannequin__label fit-mannequin__label--right"
              style={{ top: cy - 16, borderLeftColor: color }}
            >
              <span className="fit-mannequin__label-zone">{t(`fitMannequin.zones.${zoneName}`)}</span>
              <span className="fit-mannequin__label-fit">{t(`fitMannequin.fitLabels.${labelKey}`)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FitMannequin;
